// Converted from shared/design/components/SideNav.html by scripts/dc2tsx; edited by hand from here on.
import React, { Fragment } from 'react';
import { DCLogic } from '../ds/DCLogic';
import { each, ip, route, sx } from '../ds/tpl';
import { t } from '../i18n';

export interface SideNavProps {
  active?:
    | 'dashboard'
    | 'exposure'
    | 'accounts'
    | 'traders'
    | 'toxic'
    | 'cases'
    | 'alerts'
    | 'revenue'
    | 'reports'
    | 'ask'
    | 'suggestions'
    | 'news'
    | 'setup'
    | 'languages'
    | 'licence'
    | 'health'
    | 'settings'
    | (string & {});
  role?: 'owner' | 'risk' | 'dealer' | 'compliance' | 'executive' | 'readonly' | 'launcher' | (string & {});
  cases?: number;
  alerts?: number;
  news?: number;
  demo?: any;
  /** live mode: the session's permissions decide the visible items (instead of the role preset) */
  permissions?: string[];
  [key: string]: any;
}

/** Permission that shows each navigation item (GET /auth/session permissions). */
const NAV_PERM: Record<string, string[]> = {
  dashboard: ['page.dashboard'],
  exposure: ['page.exposure', 'page.ab_book'],
  accounts: ['page.accounts'],
  traders: ['page.traders'],
  toxic: ['page.toxic', 'page.detection_rules'],
  cases: ['page.cases'],
  alerts: ['page.alerts', 'page.alert_rules'],
  revenue: ['page.revenue'],
  reports: ['page.reports'],
  ask: ['page.ai'],
  suggestions: ['page.suggestions'],
  news: ['page.whats_new'],
  setup: ['launcher.setup'],
  languages: ['launcher.languages'],
  licence: ['launcher.licence'],
  health: ['page.system_health'],
};
/** First settings page the user may open, in SettingsNav order. */
const SETTINGS_ORDER: [string, string][] = [
  ['page.settings.users', '/users'],
  ['page.settings.roles', '/roles'],
  ['page.settings.channels', '/notification-channels'],
  ['page.settings.mapping', '/book-mapping'],
  ['page.settings.costs', '/costs-markup'],
  ['page.settings.general', '/general'],
  ['page.settings.setup_health', '/setup-health'],
  ['page.settings.data_api', '/data-api'],
  ['page.settings.email_templates', '/email-templates'],
  ['page.settings.audit', '/audit-log'],
];

export class SideNav extends DCLogic<SideNavProps> {
  renderVals() {
    const active = this.props.active ?? 'dashboard';
    const role = this.props.role ?? 'risk';
    const I = {
      dashboard: 'M3 3h7v7H3zM14 3h7v5h-7zM14 12h7v9h-7zM3 14h7v7H3z',
      exposure: 'M12 3 2 8l10 5 10-5-10-5ZM2 16l10 5 10-5M2 12l10 5 10-5',
      accounts: 'M3 4h18v16H3zM3 9h18M8 4v16',
      traders: 'M9 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM2 21c0-3.9 3.1-7 7-7s7 3.1 7 7M16 4a4 4 0 0 1 0 8M22 21c0-3.2-2-5.8-5-6.7',
      toxic: 'M3 12h4l3-8 4 16 3-8h4',
      cases: 'M4 4h16v16H4zM8 9h8M8 13h8M8 17h5',
      alerts: 'M6 16V11a6 6 0 0 1 12 0v5l2 2H4l2-2ZM10 21h4',
      revenue: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
      reports: 'M6 3h9l4 4v14H6zM14 3v5h5M9 13h7M9 17h5',
      ask: 'M4 5h16v11H9l-5 4V5ZM8 10h8',
      suggestions: 'M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.4 1 1.1 1 1.8V16h5v-.3c0-.7.4-1.4 1-1.8A6 6 0 0 0 12 3Z',
      news: 'M3 11v3l12 5V6L3 11ZM15 9.5a3 3 0 0 1 0 5',
      setup: 'M4 6h10M4 12h7M4 18h10M17 4l1.5 2.5L21 7l-2 2 .5 3-2.5-1.5L14.5 12l.5-3-2-2 2.5-.5Z',
      languages: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18',
      licence: 'M8 19a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM11 12l8-8M16 7l2 2M14 9l2 2',
      health: 'M3 12h4l2-5 4 10 2-5h6',
      settings:
        'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z',
    };
    const cases = this.props.cases ?? 3,
      alerts = this.props.alerts ?? 5,
      news = this.props.news ?? 2;
    const it = (id, label, href, n?, nColor?) => ({
      id,
      label,
      href,
      icon: I[id],
      n: n || 0,
      nColor: nColor || 'var(--muted)',
    });
    let groups = [
      {
        label: t('app.nav.eb-monitor', 'MONITOR'),
        items: [
          it('dashboard', t('app.nav.dashboard', 'Dashboard'), 'DSH-01_dashboard.html'),
          it('exposure', t('app.nav.exposure', 'Exposure & risk'), 'EXP-01_exposure-overview.html'),
          it('accounts', t('app.nav.accounts', 'Accounts & groups'), 'ACC-01_accounts-list.html'),
          it('traders', t('app.nav.traders', 'Traders'), 'TRD-01_traders.html'),
          it('toxic', t('app.nav.toxic', 'Toxic flow'), 'TOX-01_toxic-overview.html'),
          it('cases', t('app.nav.cases', 'Cases'), 'CAS-01_cases-list.html', cases, 'var(--warn)'),
          it('alerts', t('app.nav.alerts', 'Alerts'), 'ALR-01_alerts.html', alerts, 'var(--crit)'),
        ],
      },
      {
        label: t('app.nav.eb-insight', 'INSIGHT'),
        items: [
          it('revenue', t('app.nav.revenue', 'Revenue'), 'REV-01_revenue-overview.html'),
          it('reports', t('app.nav.reports', 'Reports'), 'RPT-01_reports-library.html'),
          it('ask', t('app.nav.ask', 'Ask Brainux'), 'AI-01_ask-brainux.html'),
          it('suggestions', t('app.nav.suggestions', 'Suggestions'), 'SUG-01_suggestions.html'),
        ],
      },
      {
        label: t('app.nav.eb-duxfx', 'DUX FX'),
        items: [it('news', t('app.nav.news', "What's New"), 'NEW-01_whats-new.html', news, 'var(--gold-text)')],
      },
    ];
    const launcherGroup = {
      label: t('app.nav.eb-launcher', 'LAUNCHER'),
      items: [
        it('setup', t('app.nav.setup', 'Setup wizard'), 'LCH-01_setup-wizard.html'),
        it('languages', t('app.nav.languages', 'Languages'), 'LCH-02_languages.html'),
        it('licence', t('app.nav.licence', 'Instance & licence'), 'LCH-03_instance-licence.html'),
        it('health', t('app.nav.health', 'System health'), 'LCH-04_system-health.html'),
      ],
    };
    const hide =
      {
        dealer: ['revenue', 'reports'],
        compliance: ['revenue'],
        executive: ['cases'],
        readonly: ['cases', 'suggestions'],
      }[role] || [];
    const perms = this.props.permissions;
    let settingsHref, settingsLabel;
    if (perms) {
      const has = (id) => (NAV_PERM[id] || []).some((x) => perms.includes(x));
      groups = [...groups, launcherGroup]
        .map((g) => ({
          label: g.label,
          items: g.items.filter((x) => has(x.id)),
        }))
        .filter((g) => g.items.length);
      const first = SETTINGS_ORDER.find(([perm]) => perms.includes(perm));
      settingsHref = first ? first[1] : '/profile';
      settingsLabel = first ? t('app.nav.settings', 'Settings') : t('app.nav.profile-settings', 'My profile & settings');
    } else {
      if (role === 'launcher') groups = [launcherGroup];
      groups = groups
        .map((g) => ({
          label: g.label,
          items: g.items.filter((x) => !hide.includes(x.id)),
        }))
        .filter((g) => g.items.length);
      settingsHref = role === 'dealer' || role === 'readonly' ? 'SET-04_profile.html' : 'SET-01_users.html';
      settingsLabel =
        role === 'dealer' || role === 'readonly'
          ? t('app.nav.profile-settings', 'My profile & settings')
          : t('app.nav.settings', 'Settings');
    }
    const bottom = [
      Object.assign(it('settings', settingsLabel, settingsHref), {
        label: settingsLabel,
      }),
    ];
    const mark = (x) =>
      Object.assign({}, x, {
        current: x.id === active ? 'page' : 'false',
        dot: x.id === active && !x.n,
      });
    return {
      rootStyle: this.props.demo ? 'min-height: 720px; background: var(--surface-3); border-inline-end: 1px solid var(--border);' : '',
      groups: groups.map((g, i) => ({
        label: g.label,
        ebCls: 'bx-nav-eb' + (i ? ' bx-nav-eb--gap' : ''),
        items: g.items.map(mark),
      })),
      bottom: bottom.map(mark),
    };
  }
  view(v: any) {
    return (
      <nav
        className="bx-nav"
        aria-label={t('c.sidenav.main', 'Main')}
        style={sx(`font-family: var(--font-sans); color: var(--ink); ${v.rootStyle ?? ''}`)}
      >
        {each(v.groups, (g, $gI) => (
          <Fragment key={$gI}>
            <span className={g?.ebCls}>{ip(g?.label)}</span>
            {each(g?.items, (it, $itI) => (
              <a key={$itI} className="bx-nav-it" href={route(it?.href)} aria-current={it?.current}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d={it?.icon} />
                </svg>
                <span>{ip(it?.label)}</span>
                {it?.n ? (
                  <span className="bx-nav-n" style={{ color: it?.nColor }}>
                    {ip(it?.n)}
                  </span>
                ) : null}
                {it?.dot ? <span className="bx-nav-dot" /> : null}
              </a>
            ))}
          </Fragment>
        ))}
        <div className="bx-nav-end">
          {each(v.bottom, (it, $itI) => (
            <a key={$itI} className="bx-nav-it" href={route(it?.href)} aria-current={it?.current}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d={it?.icon} />
              </svg>
              <span>{ip(it?.label)}</span>
              {it?.dot ? <span className="bx-nav-dot" /> : null}
            </a>
          ))}
        </div>
      </nav>
    );
  }
}
